import { getAssetUrl } from '@lighthouse/assets'
import type { Field, FieldInputType, FieldType, VariableADTvalue } from '@lighthouse/core'
import cls from 'classnames'
import React, { useEffect, useMemo } from 'react'

import { silentInputTypes, TEXT_FONT_NORMAL } from '../../../constants'
import { useFieldInteractionStyles, useFieldStyles } from '../../../hooks'
import { INPUT_FIELDS } from '../constant'
import { ErrorMessage } from '../ErrorMessage'
import type { FieldInputConfig, FieldInputError } from '../types'
import * as SC from './styles'

interface StyleContentProps {
    children?: React.ReactNode
    field?: Field
    inputType: FieldInputType
    isReadOnly?: boolean
    config: FieldInputConfig
    error?: FieldInputError
    onRenderLabel?: (value: VariableADTvalue | undefined) => string
}
const relativeSelectFieldList = new Set<FieldType>(['select', 'singleSelect'])
// 输入框的边框背景及error样式
const StyleContent = React.forwardRef<HTMLDivElement, StyleContentProps>(
    ({ children, field, inputType, config, isReadOnly, error, onRenderLabel }, ref) => {
        const { canEdit, showTitle, breakPoint } = config
        const isSelectField = field?.type ? relativeSelectFieldList.has(field.type) : false
        const fieldStyles = useFieldStyles(config, { isSelectField, parseVariableImage: onRenderLabel })
        const fontFamily = breakPoint?.font?.fontFamily
        const fieldInteractionStyle = useFieldInteractionStyles(config, { isSelectField, parseVariableImage: onRenderLabel })

        useEffect(() => {
            if (!fontFamily || fontFamily === TEXT_FONT_NORMAL) {
                return
            }
            document.fonts.ready
                .then(async ffs => {
                    const fontFaceList = [...ffs.values()]
                    const isLoaded = fontFaceList.some(item => item.family === fontFamily)
                    if (!isLoaded) {
                        const fontUrl = `url(${getAssetUrl('font', `${fontFamily}.woff2`)})`
                        const newFont = new FontFace(fontFamily, fontUrl)
                        newFont._snapdomSrc = `${getAssetUrl('font', `${fontFamily}.woff2`)}`
                        await newFont.load()
                        ffs.add(newFont)
                    }
                })

                .catch(console.warn)
        }, [fontFamily])

        // 是否有边框
        const border = useMemo(() => {
            if (config.inputType === 'relativeSelect') {
                const { relativeSelect } = config
                const { showMode } = relativeSelect ?? {}
                return showMode === 'input' || !showMode
            }
            if (config.inputType === 'calendar') {
                return false
            }
            if (config.inputType === 'notes') {
                return Boolean(canEdit)
            }
            return !silentInputTypes.has(inputType)
        }, [canEdit, config, inputType])

        const isInput = useMemo(() => {
            return INPUT_FIELDS.includes(config.inputType)
        }, [config.inputType])

        const isShowErrorBorder = useMemo(() => {
            const errorNum = error ? Object.keys(error).length : 0
            return border && errorNum > 0
        }, [border, error])

        const errorTooltipProps = useMemo(() => {
            if (error?.required) {
                return { label: error?.required?.message, disabled: false }
            }
            const errorLabel = Object.entries(error ?? {}).find(Boolean)?.[1]?.message ?? ''
            return { label: errorLabel, disabled: !errorLabel }
        }, [error])

        return (
            <SC.FieldContentContainer ref={ref}>
                <SC.FieldPreviewerContainer
                    className={cls({
                        error: isShowErrorBorder,
                        isInput
                    })}
                    fontFamily={fontFamily}
                    fieldInteractionStyle={fieldInteractionStyle}
                    fieldStyles={fieldStyles}
                    isInput={canEdit && !border}
                    style={{ minHeight: inputType === 'checkbox' ? 'auto' : undefined }}
                >
                    <ErrorMessage {...errorTooltipProps} style={{ top: showTitle ? -55 : -30 }}>
                        {children}
                    </ErrorMessage>
                </SC.FieldPreviewerContainer>
            </SC.FieldContentContainer>
        )
    }
)

export default StyleContent
